import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { RouterProvider } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { SonnerProvider } from '@/components/sonner-provider'
import '@/lib/i18n'
import './index.css'
import { router } from './router'
import { initAnalytics } from '@/lib/analytics'

// When a new build is deployed, already-loaded tabs keep the old index.html
// in memory and reference chunk hashes that no longer exist on the server.
// The first dynamic import after deploy 404s. Reload once to pick up the
// fresh index.html; guard against reload loops with a 30s cooldown.
const RELOAD_COOLDOWN_MS = 30_000
const RELOAD_KEY = 'aegis:stale-chunk-reload-at'
function reloadForStaleChunk() {
  const last = Number(sessionStorage.getItem(RELOAD_KEY) || 0)
  if (Date.now() - last < RELOAD_COOLDOWN_MS) return
  sessionStorage.setItem(RELOAD_KEY, String(Date.now()))
  window.location.reload()
}
// Patterns that indicate a stale chunk (deploy swap while tab was open) or
// a corrupted module response (e.g. a transient 503 whose body got cached by
// the browser and now presents as JSON/garbage instead of JS).
const CHUNK_ERROR_RE =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|NS_ERROR_CORRUPTED_CONTENT|not allowed .* MIME type|MIME type .* not allowed|expected a JavaScript(?:-or-Wasm)? module script/i

// Known symptom of a corrupted lazy chunk in react-router: loader returns
// `mod.default` but the module failed to parse, so `mod` is undefined →
// "Cannot read properties of undefined (reading 'default')" thrown from
// the lazy() wrapper. Only reload if the stack points at react-router lazy,
// otherwise normal TypeErrors would loop the app.
const LAZY_TYPE_ERROR_RE = /Cannot read propert(?:y|ies) of undefined \(reading ['"]default['"]\)/
function isLazyChunkTypeError(err: unknown): boolean {
  if (!(err instanceof Error)) return false
  if (!LAZY_TYPE_ERROR_RE.test(err.message)) return false
  return /router\.tsx|\blazy\b/.test(err.stack ?? '')
}

window.addEventListener('vite:preloadError', (e) => {
  e.preventDefault()
  reloadForStaleChunk()
})
window.addEventListener('unhandledrejection', (e) => {
  const reason = e.reason
  const msg = String((reason as { message?: string })?.message || reason || '')
  if (CHUNK_ERROR_RE.test(msg) || isLazyChunkTypeError(reason)) {
    e.preventDefault()
    reloadForStaleChunk()
  }
})
window.addEventListener('error', (e) => {
  // Sync error path (e.g. ES module fetch failure surfaces here in some browsers).
  const msg = String(e.message || '')
  if (CHUNK_ERROR_RE.test(msg) || isLazyChunkTypeError(e.error)) {
    e.preventDefault()
    reloadForStaleChunk()
  }
})

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
    },
  },
})

// PostHog: init prima del render così cattura attribuzione (cid/utm dall'URL)
// e identifica la persona prima dei primi eventi del funnel.
initAnalytics()

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
      <SonnerProvider />
    </QueryClientProvider>
  </StrictMode>,
)
